import React, { useEffect } from 'react';
import { Footer } from '../../components/feature/Footer';

const PrivacyAndTermsPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy and Terms of Service | SellApartmentsCash.com';
    
    // Add meta description
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Privacy Policy and Terms of Service for SellApartmentsCash.com. Read our policies on data protection, website usage, and important disclaimers about information reliability.';
    document.head.appendChild(metaDescription);
    
    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy Policy and Terms of Service
          </h1>
          <p className="text-xl opacity-90">
            Your privacy and understanding of our terms are important to us
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        
        {/* Privacy Policy Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b-2 border-orange-500 pb-4">
            Privacy Policy
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-6">
              <strong>Last Updated:</strong> January 2024
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h3>
            <p className="text-gray-700 mb-6">
              We collect information you provide directly to us, such as when you fill out a contact form, 
              request a property evaluation, or communicate with us via phone or email. This may include:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Name and contact information (phone number, email address, mailing address)</li>
              <li>Property information and details about your apartment building</li>
              <li>Financial information relevant to the potential sale of your property</li>
              <li>Communication preferences and history of interactions with our team</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h3>
            <p className="text-gray-700 mb-6">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Provide property evaluation and purchase services</li>
              <li>Communicate with you about your property and potential transactions</li>
              <li>Improve our services and customer experience</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing</h3>
            <p className="text-gray-700 mb-6">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>With service providers who assist us in operating our business</li>
              <li>When required by law or to protect our legal rights</li>
              <li>In connection with a business transfer or acquisition</li>
              <li>With your explicit consent</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h3>
            <p className="text-gray-700 mb-6">
              We implement appropriate security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. However, no method of 
              transmission over the internet is 100% secure.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking</h3>
            <p className="text-gray-700 mb-6">
              Our website may use cookies and similar tracking technologies to enhance your browsing 
              experience and analyze website traffic. You can control cookie settings through your 
              browser preferences.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h3>
            <p className="text-gray-700 mb-6">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Access and update your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Request information about how your data is used</li>
            </ul>
          </div>
        </section>

        {/* Terms of Service Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b-2 border-orange-500 pb-4">
            Terms of Service
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h3>
            <p className="text-gray-700 mb-6">
              By accessing and using this website, you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please 
              do not use this service.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Website Use</h3>
            <p className="text-gray-700 mb-6">
              You may use our website for lawful purposes only. You agree not to use the site:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>In any way that violates any applicable federal, state, local, or international law</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material</li>
              <li>To impersonate or attempt to impersonate the company or its employees</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use of the website</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Property Information and Evaluations</h3>
            <p className="text-gray-700 mb-6">
              Our property evaluations and estimates are preliminary assessments based on limited 
              information and market analysis. Final purchase offers may vary based on property 
              inspection, market conditions, and due diligence findings.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">No Agency Relationship</h3>
            <p className="text-gray-700 mb-6">
              Unless explicitly stated in a written agreement, we do not act as your real estate 
              agent or broker. We are potential purchasers of your property, and our interests 
              may differ from yours in any transaction.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h3>
            <p className="text-gray-700 mb-6">
              The content, organization, graphics, design, compilation, magnetic translation, 
              digital conversion and other matters related to the site are protected under 
              applicable copyrights, trademarks and other proprietary rights.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h3>
            <p className="text-gray-700 mb-6">
              In no event shall SellApartmentsCash.com or its suppliers be liable for any damages 
              (including, without limitation, damages for loss of data or profit, or due to 
              business interruption) arising out of the use or inability to use the materials 
              on our website.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h3>
            <p className="text-gray-700 mb-6">
              You agree to indemnify and hold harmless SellApartmentsCash.com from any claim or 
              demand, including reasonable attorneys' fees, made by any third party due to or 
              arising out of your use of our services or violation of these terms.
            </p>
          </div>
        </section>

        {/* Important Disclaimer Section */}
        <section className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            <i className="ri-alert-line text-yellow-500 mr-3"></i>
            Important Information Disclaimer
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-4 font-semibold">
              All information provided on this website is deemed reliable but not guaranteed 
              in any way, shape, or form.
            </p>
            
            <p className="text-gray-700 mb-4">
              The content on this website, including but not limited to property valuations, 
              market analysis, case studies, financial projections, and educational materials, 
              is provided for informational purposes only and should not be considered as:
            </p>
            
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Professional financial, legal, or tax advice</li>
              <li>Guaranteed property values or sale prices</li>
              <li>Promises of specific transaction outcomes</li>
              <li>Comprehensive market analysis for investment decisions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Fictional Examples and Case Studies</h3>
            <p className="text-gray-700 mb-4">
              <strong>Please note that many examples throughout this website are fictional 
              and created for educational and illustrative purposes only.</strong> These examples 
              may include:
            </p>
            
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Property case studies and success stories</li>
              <li>Financial calculations and projections</li>
              <li>Testimonials and client experiences</li>
              <li>Market scenarios and transaction examples</li>
              <li>Timeline estimates and process descriptions</li>
            </ul>

            <p className="text-gray-700 mb-4">
              While these examples are based on industry knowledge and general market conditions, 
              they do not represent actual transactions or guarantee similar results for your 
              specific situation.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Consultation Required</h3>
            <p className="text-gray-700 mb-4">
              Before making any real estate, financial, or legal decisions, we strongly recommend 
              consulting with qualified professionals including:
            </p>
            
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Licensed real estate attorneys</li>
              <li>Certified public accountants (CPAs)</li>
              <li>Licensed real estate appraisers</li>
              <li>Financial advisors and tax professionals</li>
            </ul>

            <p className="text-gray-700 font-medium">
              Each property and financial situation is unique, and results may vary significantly 
              based on numerous factors including but not limited to property condition, market 
              conditions, local regulations, and individual circumstances.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <Footer />
        
        {/* Last Updated */}
        <div className="text-center text-gray-500 text-sm mt-12 pt-8 border-t border-gray-200">
          This Privacy Policy and Terms of Service was last updated on January 15, 2024
        </div>
      </div>
    </div>
  );
};

export default PrivacyAndTermsPage;
